/** Panel đề xuất AI dùng chung Drawer + Modal inline. */
import React, { useEffect, useState } from 'react';
import Link from 'next/link';
import { Alert, Button, Checkbox, Collapse, Empty, Input, Popconfirm, Space, Typography, notification } from 'antd';
import { CheckCircleFilled, CloseCircleFilled, LoadingOutlined, ReloadOutlined, StopOutlined } from '@ant-design/icons';
import type { AnyStepAdapter } from './adapters/types';
import { aiAgentApi } from '@/services/knowledge/ai-agent-api';
import { canUseAiCoaching } from '@/components/common/AiHub/datastudio/aiKbAccess';
import { useAiHubMe } from '@/components/common/AiHub/useAiHubMe';
import { aiHubSettingsPath } from '@/config/aiAgentMenu';
import { useAuthStore } from '@/store/useAuthStore';
import { useAiStepAssistant } from './useAiStepAssistant';
import { appendChosenToPage } from './inline/bscStepAiAppend';
import type { BscApplyMode, BscResultView } from './inline/bscStepAiConfig';
import { BscProposeRichPreview } from './inline/BscProposeRichPreview';
import type { BscApplySection } from '@/components/common/AiChat/bscApplyOffer';
import styles from './AiStepAssistant.module.css';

/** Khớp `prompt_key` / child id trên engine (không dùng b1_strengths cũ). */
const ADAPTER_KEY_TO_PROMPT_ID: Record<string, string> = {
  'b1.strengths': 'company_strengths',
  'b1.csf': 'success_factor',
  'b1.competitors': 'competitors_list',
  'b1.vrio_pick': 'competitive',
  'b2.swot': 'swot',
  'b2.strategy': 'strategy',
  'b3.select': 'strategy',
  'b3.result': 'strategy',
  'b4.theme_map': 'strategy_map',
  'b4.company_map': 'strategy_map',
  'b5.fishbone': 'fishbone',
};

const stepPromptKey = (adapterKey: string) =>
  ADAPTER_KEY_TO_PROMPT_ID[adapterKey] ?? adapterKey.replace(/\./g, '_');

export type AiStepProposePanelProps = {
  adapter: AnyStepAdapter;
  onApplied?: () => void;
  onClose?: () => void;
  defaultInstruction?: string;
  /** Ẩn footer Áp dụng (modal tự xử lý). */
  hideApplyFooter?: boolean;
  hideDiffList?: boolean;
  childrenAfterProposal?: React.ReactNode;
  extraFooter?: React.ReactNode;
  applyModes?: BscApplyMode[];
  swotAppendSection?: BscApplySection;
  resultView?: BscResultView;
  compactInstruction?: boolean;
  /** Gọi Tra cứu AI ngay khi panel mount (inline card). */
  autoProposeOnMount?: boolean;
  /** Không gọi latestProposal khi mở popover/panel. */
  skipResumeOnMount?: boolean;
  /** Inline card: không ô yêu cầu/prompt, chỉ nút Tra cứu. */
  variant?: 'full' | 'compact';
};

export const AiStepProposePanel: React.FC<AiStepProposePanelProps> = ({
  adapter,
  onApplied,
  onClose,
  defaultInstruction = '',
  hideApplyFooter,
  hideDiffList,
  childrenAfterProposal,
  extraFooter,
  applyModes = ['save'],
  swotAppendSection,
  resultView = 'list',
  compactInstruction = false,
  autoProposeOnMount = false,
  skipResumeOnMount = false,
  variant = 'full',
}) => {
  const a = useAiStepAssistant(adapter, { onApplied });
  const [instruction, setInstruction] = useState(defaultInstruction);
  const [editing, setEditing] = useState<string | null>(null);
  const [promptPreview, setPromptPreview] = useState('');
  const [notifier, contextHolder] = notification.useNotification();
  // Cùng quyền với trang Cài đặt AI Agent (engine chỉ trả câu lệnh cho Admin)
  const tenantID = useAuthStore((s) => s.tenantID);
  const profile = useAuthStore((s) => s.profile);
  const { me } = useAiHubMe();
  const canEditPrompt = canUseAiCoaching(tenantID, profile, me);
  const promptKey = stepPromptKey(adapter.key);
  const requireSelection = resultView === 'vrio_pick';
  const isCompact = variant === 'compact';

  useEffect(() => {
    setInstruction(defaultInstruction);
  }, [defaultInstruction, adapter.key]);

  useEffect(() => {
    if (skipResumeOnMount && !autoProposeOnMount) return;
    void (async () => {
      const hadPending = skipResumeOnMount ? false : await a.resume();
      if (autoProposeOnMount && !hadPending) {
        void a.propose(instruction || defaultInstruction);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adapter.key, autoProposeOnMount, skipResumeOnMount]);

  useEffect(() => {
    if (isCompact || !promptKey || !canEditPrompt) {
      setPromptPreview('');
      return;
    }
    void aiAgentApi.prompt(promptKey).then((res) => {
      if (res?.success && res.data) {
        const lines = (res.data.content || '').split('\n').slice(0, 6).join('\n');
        setPromptPreview(lines);
      }
    }).catch(() => setPromptPreview(''));
  }, [promptKey, isCompact, canEditPrompt]);

  useEffect(() => {
    if (requireSelection || a.rows.length === 0) return;
    a.selectAll(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [a.rows, requireSelection]);

  const running = a.phase === 'running';
  const allSelected = a.rows.length > 0 && a.selected.size === a.rows.length;
  const applyCount = requireSelection ? a.selected.size : a.rows.filter((r) => r.op !== 'delete' && !r.warning).length;

  const onApply = async () => {
    const result = await a.apply({ all: !requireSelection });
    if (!result) return;
    const key = `ai-applied-${Date.now()}`;
    notifier.success({
      key,
      message: `Đã áp dụng ${applyCount} mục`,
      description: 'Dữ liệu đã được lưu vào trang.',
      duration: 10,
      btn: (
        <Button
          size="small"
          onClick={async () => {
            notifier.destroy(key);
            try {
              await a.undo(result);
              notifier.info({ message: 'Đã hoàn tác', duration: 3 });
            } catch (e) {
              notifier.error({ message: 'Hoàn tác thất bại', description: e instanceof Error ? e.message : '' });
            }
          }}
        >
          Hoàn tác
        </Button>
      ),
    });
    a.reset();
    onClose?.();
  };

  return (
    <div className={styles.body}>
      {contextHolder}
      {!isCompact && (
        <>
          <Input.TextArea
            value={instruction}
            onChange={(e) => setInstruction(e.target.value)}
            placeholder={
              compactInstruction
                ? 'Tuỳ chọn: bổ sung yêu cầu cho AI…'
                : 'Ví dụ: chỉ đề xuất cơ hội liên quan xuất khẩu, hoặc bám phân khúc hiện tại…'
            }
            autoSize={{ minRows: compactInstruction ? 1 : 2, maxRows: compactInstruction ? 2 : 4 }}
            className={compactInstruction ? styles.instructionCompact : undefined}
            maxLength={2000}
            disabled={running || a.phase === 'applying'}
            aria-label="Yêu cầu thêm cho AI"
          />
          {promptKey && canEditPrompt && (
            <Collapse
              size="small"
              items={[{
                key: 'prompt',
                label: `Prompt đang dùng · ${adapter.title}`,
                children: (
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <pre style={{ margin: 0, whiteSpace: 'pre-wrap', maxHeight: 120, overflow: 'auto', fontSize: 12 }}>
                      {promptPreview || '…'}
                    </pre>
                    {canEditPrompt && (
                      <Link href={aiHubSettingsPath('agents', { agent: promptKey, atab: 'prompt' })}>Sửa prompt</Link>
                    )}
                  </Space>
                ),
              }]}
            />
          )}
        </>
                          )}
      <Space>
        {running ? (
          <Button icon={<StopOutlined />} danger onClick={a.stop}>
            Dừng
          </Button>
        ) : (
          <Button
            type="primary"
            icon={a.proposal ? <ReloadOutlined /> : undefined}
            onClick={() => void a.propose(isCompact ? defaultInstruction : instruction)}
          >
            {a.proposal ? 'Tra cứu lại' : 'Tra cứu AI'}
          </Button>
        )}
      </Space>

      {a.timeline.length > 0 && !a.timeline.some((t) => t.status === 'running') ? (
        // Xong: gom thành 1 dòng (cùng kiểu bubble Action Plan), bấm để xem chi tiết
        <details className={styles.stepsSummary}>
          <summary>
            {a.timeline.some((t) => t.status === 'error') ? 'Có bước lỗi — bấm để xem' : `Đã xử lý ${a.timeline.length} bước`}
          </summary>
          <div className={styles.timeline}>
            {a.timeline.map((t) => (
              <div key={t.key} className={styles.timelineRow}>
                {t.status === 'error' ? (
                  <CloseCircleFilled style={{ color: 'var(--ai-error)' }} />
                ) : (
                  <CheckCircleFilled style={{ color: 'var(--ai-success)' }} />
                )}
                <span>{t.label}</span>
                {t.detail ? <span>· {t.detail}</span> : null}
              </div>
            ))}
          </div>
        </details>
      ) : null}
      {a.timeline.some((t) => t.status === 'running') && (
        <div className={styles.timeline} aria-live="polite">
          {a.timeline.map((t) => (
            <div key={t.key} className={styles.timelineRow}>
              {t.status === 'running' ? (
                <LoadingOutlined />
              ) : t.status === 'error' ? (
                <CloseCircleFilled style={{ color: 'var(--ai-error)' }} />
              ) : (
                <CheckCircleFilled style={{ color: 'var(--ai-success)' }} />
              )}
              <span>{t.label}</span>
              {t.detail ? <span>· {t.detail}</span> : null}
            </div>
          ))}
        </div>
      )}

      {a.error && <Alert type="error" showIcon message={a.error} />}

      {a.proposal && (
        // Khung thẻ đề xuất thống nhất với thẻ thao tác Action Plan V2 (header · nội dung · nút bên phải)
        <section className={styles.proposalCard} aria-label="Đề xuất của AI">
          <header className={styles.proposalHead}>
            <span className={styles.proposalTitle}>Đề xuất của AI</span>
            {a.rows.length ? <span className={styles.proposalCount}>{a.rows.length} mục</span> : null}
          </header>
          <div className={styles.proposalBody}>
          {a.proposal.summary ? (
            <Typography.Paragraph type="secondary" style={{ marginBottom: 8 }}>
              {a.proposal.summary}
            </Typography.Paragraph>
          ) : null}
          {a.proposal.warnings.length > 0 && (
            <Alert
              type="warning"
              showIcon
              message="Lưu ý"
              description={
                <ul style={{ margin: 0, paddingLeft: 18 }}>
                  {a.proposal.warnings.map((w) => (
                    <li key={w}>{w}</li>
                  ))}
                </ul>
              }
            />
          )}
          {childrenAfterProposal}
          {!hideDiffList && (!childrenAfterProposal || a.rows.length > 0) && (
            a.rows.length === 0 ? (
              !childrenAfterProposal ? (
                <Empty description={isCompact ? 'AI chưa đề xuất mục nào. Bấm Tra cứu AI để thử lại.' : 'AI chưa đề xuất mục nào. Thử Tra cứu lại hoặc ghi yêu cầu thêm.'} />
              ) : null
            ) : (
              <>
                {requireSelection ? (
                  <Checkbox checked={allSelected} indeterminate={!allSelected && a.selected.size > 0} onChange={(e) => a.selectAll(e.target.checked)}>
                    Chọn tất cả ({a.selected.size}/{a.rows.length})
                  </Checkbox>
                ) : null}
                <BscProposeRichPreview
                  resultView={resultView}
                  proposal={a.proposal!}
                  rows={a.rows}
                  selected={a.selected}
                  toggle={a.toggle}
                  edits={a.edits}
                  edit={a.edit}
                  editing={editing}
                  setEditing={setEditing}
                  hideSelection={!requireSelection}
                />
              </>
            )
          )}
          {(!hideApplyFooter || extraFooter) && (
            <div className={styles.footer}>
              {extraFooter}
              {!hideApplyFooter && applyModes.includes('append') && (
                <Button
                  disabled={applyCount === 0}
                  onClick={async () => {
                    try {
                      const refs = requireSelection ? a.selected : new Set(a.rows.filter((r) => r.op !== 'delete' && !r.warning).map((r) => r.ref));
                      await appendChosenToPage(adapter, a.proposal!, a.rows, refs, a.edits, swotAppendSection);
                      notifier.success({ message: 'Đã thêm vào trang (chưa lưu) — kiểm tra rồi bấm Lưu trên trang', duration: 4 });
                      onClose?.();
                    } catch (e) {
                      notifier.error({ message: e instanceof Error ? e.message : 'Không thêm được' });
                    }
                  }}
                >
                  Thêm vào trang
                </Button>
              )}
              {!hideApplyFooter && applyModes.includes('save') && (
              <>
              <Button type="text" onClick={() => void a.reject()}>Bỏ qua đề xuất</Button>
              <Popconfirm
                title={requireSelection ? `Áp dụng ${applyCount} mục vào trang?` : 'Lưu đề xuất AI vào trang?'}
                description="Dữ liệu sẽ được lưu ngay; có thể hoàn tác trong 10 giây."
                okText={requireSelection ? 'Chọn & lưu' : 'Lưu'}
                cancelText="Huỷ"
                onConfirm={() => void onApply()}
                disabled={applyCount === 0}
              >
                <Button type="primary" loading={a.phase === 'applying'} disabled={applyCount === 0}>
                  {requireSelection ? `Chọn & lưu (${applyCount})` : 'Lưu'}
                </Button>
              </Popconfirm>
              </>
              )}
            </div>
          )}
          </div>
        </section>
      )}
    </div>
  );
};

/** Expose assistant hook state for modal footers */
export type AiStepProposePanelHandle = {
  propose: (instruction: string) => Promise<void>;
  apply: () => Promise<unknown>;
  assistant: ReturnType<typeof useAiStepAssistant>;
};

export default AiStepProposePanel;
